import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_CartoonGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/cartoon-generator',
			name: 'AI Cartoon Generator | Turn Photos into Cartoons Online | Lift',
			description:
				'Turn your photos into fun AI cartoons in seconds. No watermark.',
			url: 'https://liftapp.ai/cartoon-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/cartoon-generator#product' },
				{ '@id': 'https://liftapp.ai/cartoon-generator#howto' },
				{ '@id': 'https://liftapp.ai/cartoon-generator#benefits' },
				{ '@id': 'https://liftapp.ai/cartoon-generator#faq' },
				{ '@id': 'https://liftapp.ai/cartoon-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/cartoon-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/cartoon-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/cartoon-generator#product',
			name: 'AI Cartoon Generator',
			description:
				'Turn your photos into fun AI cartoons in seconds. No watermark.',
			url: 'https://liftapp.ai/cartoon-generator',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: 4.8,
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/cartoon-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: 'Choose a photo from your device or take one using Lift’s app',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'We create a custom AI style',
					text: 'Our AI takes your photo, keeps your features, and transforms it into a playful cartoon version — from Pixar-like portraits to comic-style characters',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/cartoon-generator#benefits',
			name: 'Who can benefit from AI Cartoon Generator?',
			description:
				'AI Cartoon Generator is ideal for anyone looking to turn their photos into fun, creative cartoon versions — for personal use, content creation, or branding.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'The AI Cartoon Generator is perfect for anyone who wants fun, creative versions of their photos',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Social media users can make their profiles stand out with unique cartoon avatars',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Friends and families can turn everyday selfies into hilarious, shareable moments',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Artists, marketers, and creators can experiment with styles for projects or campaigns',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'This feature delivers instant, trendy results — a must-try for both personal fun and creative work',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.e4b8f8be.webp',
				name: 'Simpsons-style dad kissing daughter on the cheek',
				description: 'Dad and daughter reimagined as Simpsons characters',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/cartoon-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Cartoon Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Cartoon Generator is a premium online tool that transforms your photos into cartoon-style portraits — perfect for social media, gifts, or just for fun.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Cartoon Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Our AI analyzes your photo, keeps your real features, and applies a chosen cartoon style. Everything happens automatically in the cloud — no manual editing or drawing needed.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep my face and details recognizable?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI Cartoon Generator preserves your facial features so the cartoon still looks like you, just in a stylized way — with accurate hair, eyes, and expressions.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/cartoon-generator#why-lift',
			name: 'Why Lift’s AI Cartoon Generator stands out',
			description:
				'Explore what makes Lift’s AI-powered Cartoon Generator unique — from studio-quality results to cross-platform support and affordability.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Professional results',
					description:
						'Achieve studio-quality images without the need for complex tools',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI accuracy',
					description:
						'Smart algorithms deliver enhancements tailored to your specific image',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly editing services with Lift’s affordable solution',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.d201420d.webp',
				name: 'Pixar-like animated portrait',
				description:
					'3D animated portrait of a stylish young woman reimagined in cartoon style, AI accuracy, multi-platform access, and cost savings.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/cartoon-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/cartoon-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Object Remover',
					item: 'https://liftapp.ai/cartoon-generator',
				},
			],
		},
	],
};
